import { Component, HostListener } from '@angular/core';
import { OwlOptions } from 'ngx-owl-carousel-o';
import emailjs, { type EmailJSResponseStatus } from '@emailjs/browser';

export interface Feature {
  icon: string;
  title: string;
  desc: string;
}

export interface Work {
  img: string;
  title: string;
  category: string;
}

export interface Pricing {
  icon: string;
  name: string;
  price: string;
  features: { value: string; text: string; available: boolean }[];
}

@Component({
  standalone: false,
  selector: 'app-home-four',
  templateUrl: './home-four.component.html',
  styleUrl: './home-four.component.css'
})
export class HomeFourComponent {
  // Header Transparent
  isTransparent = true;

  // Skills animation trigger
  isScrolled = false;

  @HostListener('window:scroll', [])
  onWindowScroll(): void {

    const scrollY = window.scrollY;

    // Header transparency
    this.isTransparent = scrollY < 100;


    // Skills section scroll animation
    const skillsSection = document.querySelector('.skill');

    if (skillsSection) {

      const position = skillsSection.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;

      if (position < windowHeight) {
        this.isScrolled = true;
      }

    }

  }


  // Header Menu
  menuItems = [
    { name: 'Home', link: 'home' },
    { name: 'About Us', link: 'about' },
    { name: 'Our Work', link: 'work' },
    { name: 'Our Pricing', link: 'pricing' },
    { name: 'Contact Us', link: 'contact' }
  ];


  // Social Links
  socialLinks = [
    { icon: 'bi bi-facebook', link: '/' },
    { icon: 'bi bi-twitter-x', link: '/' },
    { icon: 'bi bi-linkedin', link: '/' }
  ];


  // One Page Scroll
  scrollToSection(sectionId: string) {

    const element = document.getElementById(sectionId);

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }

  }


  // Video Popup
  isOpen = false;

  openPopup(): void {
    this.isOpen = true;
  }

  closePopup(): void {
    this.isOpen = false;
  }


  // Features
  features: Feature[] = [
    {
      icon: 'bulb-shadow-icon',
      title: 'Creative Design',
      desc: 'Integer purus ipsum, auctor vitae posuere et, consectetur ac leo.'
    },
    {
      icon: 'diamond-shadow-icon',
      title: 'Development',
      desc: 'Integer purus ipsum, auctor vitae posuere et, consectetur ac leo.'
    },
    {
      icon: 'magic-shadow-icon',
      title: 'Implementation',
      desc: 'Integer purus ipsum, auctor vitae posuere et, consectetur ac leo.'
    },
    {
      icon: 'connect-shadow-icon',
      title: 'Customer Support',
      desc: 'Integer purus ipsum, auctor vitae posuere et, consectetur ac leo.'
    }
  ];


  // Skills
  skills = [
    { name: 'Visual Design', level: 86 },
    { name: 'React', level: 90 },
    { name: 'Figma', level: 68 },
    { name: 'Development', level: 75 },
    { name: 'Production', level: 56 }
  ];

  // Works
  works:Work[] =[
    {
      img:'1.jpg',
      title:'Amazing Illustration',
      category:'Web'
    },
    {
      img:'2.jpg',
      title:'Flyer Design',
      category:'Graphic'
    },
    {
      img:'3.jpg',
      title:'Dafur Dagegen',
      category:'Brand'
    },
    {
      img:'4.jpg',
      title:'Motherlands Cars',
      category:'Graphics'
    },
    {
      img:'5.jpg',
      title:'T-shirt Design',
      category:'Wordpress'
    },
    {
      img:'6.jpg',
      title:'Business Cards',
      category:'Graphic'
    }
  ];

  // Funfacts
  funFacts =[
    {
      icon: 'bi-briefcase-fill red-color',
      number: 100,
      desc: "Our Projects",
      displayedNumber: 0
    },
    {
      icon: 'bi-people-fill purple-color',
      number: 350,
      desc: "Happy Clients",
      displayedNumber: 0
    },
    {
      icon: 'bi-cup-hot-fill green-color',
      number: 574,
      desc: "Cups of Coffee",
      displayedNumber: 0
    },
    {
      icon: 'bi-hand-thumbs-up yellow-color',
      number: 1000,
      desc: "Facebook Likes",
      displayedNumber: 0
    }
  ]

  ngOnInit(): void {
    this.startCounters();
  }

  startCounters(): void {
    this.funFacts.forEach(fact => {
      this.animateCounter(fact);
    });
  }

  animateCounter(fact: any): void {
    const duration = 4000; 
    const intervalTime = 50; 
    const increment = fact.number / (duration / intervalTime);

    const interval = setInterval(() => {
      fact.displayedNumber += increment;
      if (fact.displayedNumber >= fact.number) {
        fact.displayedNumber = fact.number;
        clearInterval(interval); 
      }
    }, intervalTime);
  }

  //Pricing Tables
  pricingPlans: Pricing[] = [
    {
      icon: 'bi bi-rocket-takeoff',
      name: 'Basic',
      price: '$195.99',
      features: [
        { value: '15', text: 'Email Account', available: true },
        { value: '100GB', text: 'Space', available: true },
        { value: '1', text: 'Domain Name', available: true },
        { value: '500GB', text: 'Bandwidth', available: true },
        { value: '25', text: 'MySQL Databases', available: true },
        { value: 'Unlimited', text: 'Subdomains', available: false }
      ]
    },
    {
      icon: 'bi bi-diamond-half',
      name: 'Standard',
      price: '$195.99',
      features: [
        { value: '15', text: 'Email Account', available: true },
        { value: '100GB', text: 'Space', available: true },
        { value: '1', text: 'Domain Name', available: true },
        { value: '500GB', text: 'Bandwidth', available: true },
        { value: '25', text: 'MySQL Databases', available: true },
        { value: 'Unlimited', text: 'Subdomains', available: false }
      ]
    },
    {
      icon: 'bi bi-pie-chart-fill',
      name: 'Ultimate',
      price: '$195.99',
      features: [
        { value: '15', text: 'Email Account', available: true },
        { value: '100GB', text: 'Space', available: true },
        { value: '1', text: 'Domain Name', available: true },
        { value: '500GB', text: 'Bandwidth', available: true },
        { value: '25', text: 'MySQL Databases', available: true },
        { value: 'Unlimited', text: 'Subdomains', available: false }
      ]
    }
  ];

  // OWl Slider
  testimonialOptions: OwlOptions = {
    loop: true,
    dots: true,
    margin:30,
    nav:false,
    autoplay:true,
    autoplaySpeed: 2000,
    navSpeed: 2000,
    items: 3,
    navText: ['<i class="bi bi-arrow-left"></i>', '<i class="bi bi-arrow-right"></i>'],
  }

  testimonials = [
    {
      name: "Melina Walber",
      position: "manager@Chaos.io",
      image: "avatar-4.jpg",
      desc:"Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus."
    },

    {
      name: "Doris Campos",
      position: "founder@Chaos.io",
      image: "avatar-5.jpg",
      desc:"Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus."
    },

    {
      name: "Melina Walber",
      position: "manager@Chaos.io",
      image: "avatar-6.jpg",
      desc:"Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus."
    },

    {
      name: "Doris Campos",
      position: "founder@Chaos.io",
      image: "avatar-1.jpg",
      desc:"Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus."
    }
  ];

  // Contact Form
  formData = {
    user_name: '',
    user_email: '',
    message: ''
  };

  // EmailJS service configuration
  private serviceId = 'helifax-form';  // Replace with your EmailJS Service ID
  private templateId = 'helifax-form';  // Replace with your EmailJS Template ID
  private publicKey = 'dOfh1ImCGDZCO9GJb';  // Replace with your EmailJS Public Key

  // Method to send email on form submission
  public sendEmail(event: Event): void {
    event.preventDefault();

    // Send email using EmailJS
    emailjs.sendForm(this.serviceId, this.templateId, event.target as HTMLFormElement, this.publicKey)
      .then(
        (response: EmailJSResponseStatus) => {
          alert('Message sent successfully. Thank you, we will get back to you soon!');
          this.resetForm();
        },
        (error: EmailJSResponseStatus) => {
          console.error('Failed to send message. Error:', error.text);
          alert('Failed to send the message. Please try again later.');
        }
      );
  }

  // Method to reset the form after submission
  private resetForm(): void {
    this.formData = {
      user_name: '',
      user_email: '',
      message: ''
    };
  }
}
